import type {
  AddLocationCommentData,
  AddLocationFormData,
  BaseLocationDto,
  CityDto,
  CommentDto,
  DetailLocationDto,
  GeoCoords,
  GooglePlaceQuery,
  GooglePlaceResultDto,
  ImageDto,
  MapLocationDto,
  SearchLocationDto,
  SearchLocationOptions,
} from '@/core/data/location/location.interface';
import type { SparkSegment } from '@/core/data/spark/spark.interface';
import { LocationEndpoint, SparksEndpoint } from '@/core/network/api/constants/api.url.constant';
import {
  baseLocationFields,
  baseMarkerFields,
  detailsLocationFields,
  detailsLocationImagesFields,
  locationSearchFields,
  publicLocationFields,
} from '@/core/network/api/constants/location.api_fields';
import { HttpClient } from '@/core/network/http/httpClient';
import type {
  GamResponse,
  GetCoords,
  GetLocationTableFilters,
  GetSparkableLocationTableFilters,
  GetTableParams,
  SearchLocationParams,
} from '@/core/network/http/httpClient.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useFilterStore } from '@/stores/filter.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useMapStore } from '@/stores/map.store';
import { useTableStore } from '@/stores/table.store';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamFilterSortLocation } from '@/views/composables/constants/main/filter.constants';
import type { AxiosResponse } from 'axios';
import { storeToRefs } from 'pinia';

export class LocationApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  private getLocationsParams(id: GamListId, geo: GeoCoords, radius: number): GetLocationTableFilters {
    const tableStore = useTableStore(id)();
    const filterStore = useFilterStore<GetLocationTableFilters>(GamListId.LOCATIONS)();
    const { userLocation } = storeToRefs(useGeolocationStore());

    const userLatitude = geo.lat || userLocation.value?.location.lat;
    const userLongitude = geo.lng || userLocation.value?.location.lng;

    return {
      ...tableStore.tableParams,
      ...tableStore.getLocationParam(),
      ...filterStore.getFilters(),
      latitude: userLatitude || tableStore.getLocationParam()?.latitude || filterStore.getFilters()?.latitude,
      longitude: userLongitude || tableStore.getLocationParam()?.longitude || filterStore.getFilters()?.longitude,
      userLatitude,
      userLongitude,
      radius: radius,
    };
  }

  private getLocationFields() {
    const { user } = storeToRefs(useAuthStore());

    return user.value ? baseLocationFields.join(',') : publicLocationFields.join(',');
  }

  // async getCity(city: string, country: string, coordinates: GeoCoords): Promise<AxiosResponse<GamResponse<CityDto>>> {
  //   return this.httpClient.get<CityDto, FindCityParams>(LocationEndpoint.CITY, {
  //     config: {
  //       params: {
  //         city,
  //         country,
  //         ...coordinates,
  //       },
  //     },
  //   });
  // }

  async getCityFor(coordinates: GeoCoords): Promise<AxiosResponse<GamResponse<CityDto>>> {
    return this.httpClient.get<CityDto, GetCoords>(LocationEndpoint.CITY_FOR, {
      config: {
        params: {
          ...coordinates,
        },
      },
    });
  }

  async getLocations(
    listId: GamListId,
    geo: GeoCoords,
    radius: number,
  ): Promise<AxiosResponse<GamResponse<BaseLocationDto[]>>> {
    return this.httpClient.get<BaseLocationDto[], GetLocationTableFilters>(LocationEndpoint.GET_LOCATIONS, {
      config: {
        params: {
          ...this.getLocationsParams(listId, geo, radius),
          sortBy: GamFilterSortLocation.DISTANCE,
          fields: this.getLocationFields(),
        },
      },
    });
  }

  async getLocationsByCoordinates(
    geo: GeoCoords,
    options?: {
      distance?: number;
      perPage?: number;
      intentDate?: string;
      availabilityDate?: string;
    },
  ): Promise<AxiosResponse<GamResponse<BaseLocationDto[]>>> {
    return this.httpClient.get<BaseLocationDto[], GetLocationTableFilters>(LocationEndpoint.GET_LOCATIONS, {
      config: {
        params: {
          latitude: geo.lat,
          longitude: geo.lng,
          userLatitude: geo.lat,
          userLongitude: geo.lng,
          radius: options?.distance,
          perPage: options?.perPage,
          intentDate: options?.intentDate,
          availabilityDate: options?.availabilityDate,
          sortBy: GamFilterSortLocation.DISTANCE,
          fields: locationSearchFields.join(','),
        },
      },
    });
  }

  async getSparkableLocationsByCoordinates(
    geo: GeoCoords,
    options?: {
      distance?: number;
      perPage?: number;
      intentDate?: string;
      availabilityDate?: string;
    },
  ): Promise<AxiosResponse<GamResponse<BaseLocationDto[]>>> {
    return this.httpClient.get<BaseLocationDto[], GetSparkableLocationTableFilters>(
      LocationEndpoint.GET_LOCATIONS_SPARKABLE,
      {
        config: {
          params: {
            lat: geo.lat,
            lng: geo.lng,
            distance: options?.distance,
            perPage: options?.perPage,
            intentDate: options?.intentDate,
            availabilityDate: options?.availabilityDate,
            fields: locationSearchFields.join(','),
          },
        },
      },
    );
  }

  async getAvailabilities(
    locationIds: string[],
    day: number,
    startTime: number,
    endTime: number,
  ): Promise<AxiosResponse<GamResponse<SparkSegment[]>>> {
    return this.httpClient.get<any[]>(SparksEndpoint.SPARK_MATCHES, {
      config: {
        params: {
          locationIds: locationIds.join(','),
          start: startTime,
          end: endTime,
          day: day,
        },
      },
    });
  }

  async getMapLocations(
    listId: GamListId,
    geo: GeoCoords,
    radius: number,
  ): Promise<AxiosResponse<GamResponse<MapLocationDto[]>>> {
    return this.httpClient.get<MapLocationDto[], GetLocationTableFilters>(LocationEndpoint.GET_LOCATIONS_MAP, {
      config: {
        params: {
          ...this.getLocationsParams(listId, geo, radius),
          fields: baseMarkerFields.join(','),
        },
      },
    });
  }

  async fetchGooglePlaceDetails(data: GooglePlaceQuery): Promise<AxiosResponse<GamResponse<GooglePlaceResultDto>>> {
    return await this.httpClient.post(LocationEndpoint.GOOGLE_PLACE_DETAILS, {
      urlParams: { id: data.placeId },
      data,
    });
  }

  async reverseGeocode(latitude: number, longitude: number): Promise<AxiosResponse<GamResponse<SearchLocationDto>>> {
    return await this.httpClient.get(LocationEndpoint.GOOGLE_REVERSE_GEOCODE, {
      config: {
        params: {
          latitude,
          longitude,
        },
      },
    });
  }

  // TODO: Remove fallback location
  async searchLocationsByQuery(
    query?: string,
    options?: SearchLocationOptions,
  ): Promise<AxiosResponse<GamResponse<SearchLocationDto[]>>> {
    const mapStore = useMapStore();

    return this.httpClient.get<SearchLocationDto[], SearchLocationParams>(LocationEndpoint.AUTOCOMPLETE_LOCATIONS, {
      config: {
        params: {
          query,
          internal: options?.internal,
          mapbox: options?.mapbox,
          latitude: options?.geo?.lat || mapStore.currentLocation?.lat,
          longitude: options?.geo?.lng || mapStore.currentLocation?.lng,
          onlyCities: options?.onlyCities,
          sessionToken: options?.sessionToken,
        },
      },
    });
  }

  async getLocation(id: string): Promise<AxiosResponse<GamResponse<DetailLocationDto>>> {
    return this.httpClient.get<DetailLocationDto, GetTableParams>(LocationEndpoint.GET_LOCATION_DETAILS, {
      config: {
        params: {
          fields: detailsLocationFields.join(','),
        },
      },
      urlParams: { id },
    });
  }

  async getLocationCover(id: string): Promise<AxiosResponse<GamResponse<string>>> {
    return this.httpClient.get<string>(LocationEndpoint.LOCATION_COVER, {
      urlParams: { id },
    });
  }

  async getLocationImages(id: string): Promise<AxiosResponse<GamResponse<ImageDto[]>>> {
    return this.httpClient.get<ImageDto[], GetTableParams>(LocationEndpoint.LOCATION_IMAGES, {
      urlParams: { id },
      config: {
        params: {
          fields: detailsLocationImagesFields.join(','),
        },
      },
    });
  }

  // async getLocationComments(id: string): Promise<AxiosResponse<GamResponse<CommentDto[]>>> {
  //   return this.httpClient.get<CommentDto[], GetTableParams>(LocationEndpoint.LOCATION_COMMENTS, {
  //     urlParams: { id },
  //     config: {
  //       params: {
  //         fields: detailsLocationCommentsFields.join(','),
  //       },
  //     },
  //   });
  // }

  async createFirestoreDoc(id: string): Promise<AxiosResponse<GamResponse<any>>> {
    return await this.httpClient.put(LocationEndpoint.LOCATION_FIRESTORE, {
      urlParams: { id },
    });
  }

  async addLocation(data: AddLocationFormData): Promise<AxiosResponse<GamResponse<BaseLocationDto>>> {
    const { openingHours, ...rest } = data;
    return await this.httpClient.post(LocationEndpoint.GET_LOCATIONS, {
      data: {
        ...rest,
        ...(openingHours !== undefined && { openingHours: JSON.stringify(openingHours) }),
      },
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  // async updateLocation(data: AddLocationFormData): Promise<AxiosResponse<GamResponse<BaseLocationDto>>> {
  //   return await this.httpClient.put(LocationEndpoint.GET_LOCATIONS, {
  //     data,
  //     config: {
  //       headers: {
  //         'Content-Type': 'multipart/form-data',
  //       },
  //     },
  //   });
  // }

  async toggleFavorite(id: string, favorite?: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    if (favorite) {
      return await this.httpClient.post(LocationEndpoint.LOCATION_FAVORITES, {
        urlParams: { id },
      });
    } else {
      return await this.httpClient.delete(LocationEndpoint.LOCATION_FAVORITES, {
        urlParams: { id },
      });
    }
  }

  async toggleImageLike(id: string, like: boolean): Promise<AxiosResponse<GamResponse<void>>> {
    if (like) {
      return await this.httpClient.post(LocationEndpoint.LOCATION_IMAGES_LIKE, {
        urlParams: { id },
      });
    } else {
      return await this.httpClient.delete(LocationEndpoint.LOCATION_IMAGES_LIKE, {
        urlParams: { id },
      });
    }
  }

  async deleteLocationImage(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(LocationEndpoint.LOCATION_IMAGE, {
      urlParams: { id },
    });
  }

  async addPhoto(id: string, photo?: File | string | null): Promise<AxiosResponse<GamResponse<ImageDto>>> {
    return await this.httpClient.post(LocationEndpoint.LOCATION_IMAGES, {
      urlParams: { id },
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      data: {
        image: photo,
      },
    });
  }

  async pinCover(
    id: string,
    imageBlobHash: string | null,
  ): Promise<AxiosResponse<GamResponse<{ coverBlobHash: string | null }>>> {
    return await this.httpClient.patch(LocationEndpoint.LOCATION_PIN_COVER, {
      urlParams: { id },
      data: { imageBlobHash },
    });
  }

  async addComment(id: string, data: AddLocationCommentData): Promise<AxiosResponse<GamResponse<CommentDto>>> {
    return await this.httpClient.post(LocationEndpoint.LOCATION_COMMENTS, {
      urlParams: { id },
      data,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });
  }

  async deleteComment(id: string): Promise<AxiosResponse<GamResponse<void>>> {
    return await this.httpClient.delete(LocationEndpoint.LOCATION_COMMENT_DELETE, {
      urlParams: { id },
    });
  }

  // async getUserLocation(apiKey: string): Promise<AxiosResponse<GamResponse<UserGeoData>>> {
  //   return this.httpClient.post<UserGeoData, GetTableParams>(this.getGeoLocationUrl.replace(/%s/g, apiKey));
  // }

  async checkIn(
    id: string,
    options?: { gambitId?: string; ignoreGambit?: boolean },
  ): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${LocationEndpoint.CHECK_IN}`;

    const queryParams: { gambitId?: string; ignoreGambit?: string } = {};

    if (options?.gambitId) {
      queryParams.gambitId = options.gambitId;
    }

    if (options?.ignoreGambit) {
      queryParams.ignoreGambit = 'true';
    }

    return await this.httpClient.post(url, {
      urlParams: { id },
      queryParams,
    });
  }

  async updateCheckIn(id: string, data: { chessSetCount?: number }): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${LocationEndpoint.CHECK_IN}`;

    return await this.httpClient.patch(url, {
      urlParams: { id },
      data,
    });
  }

  async checkOut(id: string): Promise<AxiosResponse<GamResponse<any>>> {
    const url = `${LocationEndpoint.CHECK_IN}`;

    return await this.httpClient.delete(url, {
      urlParams: { id },
    });
  }
}
